import type { BadgeProps } from '@/components/ui/badge'
import type { CVDocument } from '@/hooks/use-cv-upload'
import { formatDistanceToNow } from 'date-fns'

type BadgeVariant = NonNullable<BadgeProps['variant']>

export type CvStatusFilter = 'all' | 'completed' | 'processing' | 'failed'

export const CV_STATUS_FILTERS: { value: CvStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Parsed' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
]

export function cvStatusBadgeVariant(status: CVDocument['parsing_status']): BadgeVariant {
  switch (status) {
    case 'pending':
    case 'processing':
      return 'secondarySoft'
    case 'completed':
      return 'successSoft'
    case 'failed':
      return 'destructiveSoft'
    default:
      return 'secondarySoft'
  }
}

export function cvStatusFilterCounts(docs: CVDocument[]) {
  const processing = docs.filter(
    (d) => d.parsing_status === 'pending' || d.parsing_status === 'processing',
  ).length
  return {
    all: docs.length,
    completed: docs.filter((d) => d.parsing_status === 'completed').length,
    processing,
    failed: docs.filter((d) => d.parsing_status === 'failed').length,
  } satisfies Record<CvStatusFilter, number>
}

export function filterCvDocuments(docs: CVDocument[], filter: CvStatusFilter): CVDocument[] {
  if (filter === 'all') return docs
  if (filter === 'completed') return docs.filter((d) => d.parsing_status === 'completed')
  if (filter === 'processing') {
    return docs.filter((d) => d.parsing_status === 'pending' || d.parsing_status === 'processing')
  }
  return docs.filter((d) => d.parsing_status === 'failed')
}

export function formatCvStatusLabel(status: CVDocument['parsing_status']): string {
  if (status === 'completed') return 'Parsed'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function formatCvStatusPillLabel(label: string, count: number): string {
  return count > 0 ? `${label} (${count})` : label
}

export function formatCvFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
}

export function formatCvRelativeDate(dateString: string): string {
  return formatDistanceToNow(new Date(dateString), { addSuffix: true })
}

/** Prefer in-flight parsing, then latest completed, then latest failed. */
export function getPrimaryCvDocument(docs: CVDocument[]): CVDocument | null {
  if (docs.length === 0) return null

  const inProgress = docs.find(
    (d) => d.parsing_status === 'pending' || d.parsing_status === 'processing',
  )
  if (inProgress) return inProgress

  const completed = docs
    .filter((d) => d.parsing_status === 'completed')
    .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())
  if (completed.length > 0) return completed[0]

  const failed = docs
    .filter((d) => d.parsing_status === 'failed')
    .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())
  return failed[0] ?? null
}
